<template>
    <Modal
        ref="modal"
        title="Raise Sales Invoice"
        size="is-xxlarge"
        :close-enable="!isLoading && !isUploading"
        :loading="isLoading || isUploading"
        paddingless
        @closing="resetModal()"
    >
        <template #loading>
            <LoadingSpinner v-if="!isUploading" label="Creating invoice" />
            <div v-else class="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-3 tw-p-5 tw-text-gray-400">
                <LoadingSpinner label="Uploading attachment" />
                <div class="tw-w-full tw-max-w-md tw-text-center">
                    <div class="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-sm">
                        <span class="tw-truncate">{{ uploadFileName }}</span>
                        <span class="tw-shrink-0">{{ uploadProgress }}%</span>
                    </div>
                    <ProgressBar class="tw-h-3 tw-shadow-sm" :progress="uploadProgress" />
                </div>
            </div>
        </template>
        <div v-if="revenueEntry">
            <div v-if="!hasSavedSupplier" class="tw-p-3">
                <Banner type="is-warning" class="tw-mb-3">
                    Please complete the address fields for
                    <strong>{{ revenueEntry.supplier.name }}</strong>
                    before continuing
                </Banner>
                <FormField label="Company Name (required)" class="tw-mb-2">
                    <Textbox v-model="revenueEntry.supplier.name" placeholder="Name" required />
                </FormField>
                <FormField label="Address Line 1" class="tw-mb-2">
                    <Textbox v-model="revenueEntry.supplier.address1" placeholder="Address Line 1" />
                </FormField>
                <FormField label="Address Line 2" class="tw-mb-2">
                    <Textbox v-model="revenueEntry.supplier.address2" placeholder="Address Line 2" />
                </FormField>
                <div class="tw-flex tw-mb-2">
                    <FormField class="tw-flex-1 pr-3" label="City">
                        <Textbox v-model="revenueEntry.supplier.city" placeholder="City" />
                    </FormField>
                    <FormField label="Postal Code">
                        <Textbox v-model="revenueEntry.supplier.postal_code" placeholder="Postal Code" />
                    </FormField>
                </div>
                <div class="d-flex" style="gap: 0.75rem">
                    <FormField style="width: 50%" label="Country">
                        <Select v-model="revenueEntry.supplier.country" placeholder="Country" expanded>
                            <option v-for="country in countries" :key="country" :value="country">
                                {{ country }}
                            </option>
                        </Select>
                    </FormField>
                    <FormField style="width: 50%" label="VAT Number">
                        <Textbox v-model="revenueEntry.supplier.tax_number" placeholder="VAT Number" />
                    </FormField>
                </div>
            </div>

            <template v-else-if="step === 0">
                <Banner v-if="!uninvoicedLineItemCount" class="tw-mx-4 tw-my-3" type="is-warning">
                    <i class="mdi mdi-alert-circle-outline"></i>
                    It looks like all available line items on this revenue entry have been invoiced.
                </Banner>
                <DataTable
                    v-model="selectedLineItems"
                    :data="availableLineItems"
                    :columns="[
                        {
                            title: 'Description',
                            field: 'description',
                        },
                        {
                            title: 'Qty',
                            field: 'quantity',
                            headerClass: '!tw-text-right',
                            class: '!tw-text-right',
                        },
                        {
                            title: 'Unit Price',
                            field: 'unit_price',
                            headerClass: '!tw-text-right',
                            class: '!tw-text-right',
                        },
                        {
                            title: 'Tax Rate',
                            field: 'tax_rate_id',
                            headerClass: '!tw-text-right',
                            class: '!tw-text-right',
                        },
                        {
                            title: 'Subtotal',
                            field: 'subtotal',
                            headerClass: '!tw-text-right',
                            class: '!tw-text-right',
                        },
                        {
                            title: 'Total',
                            field: 'total',
                            headerClass: '!tw-text-right',
                            class: '!tw-text-right',
                        },
                    ]"
                    checkable
                    @update:model-value="loadItemsIntoForm"
                >
                    <template #column(description)="{ row }">
                        {{ row.description }}
                    </template>
                    <template #column(quantity)="{ row }">
                        {{ row.quantity }}
                    </template>
                    <template #column(unit_price)="{ row }">
                        {{ formatCurrency(row.unit_price, 2, store.get('current_event.currency_code')) }}
                    </template>
                    <template #column(tax_rate_id)="{ row }">
                        <span v-if="row.tax_rate_id">
                            {{ localise(store.get(`account.tax_rates.${row.tax_rate_id}`).rate, 2, false) }}%
                        </span>
                        <span v-else class="tw-text-gray-400">None</span>
                    </template>
                    <template #column(subtotal)="{ row }">
                        {{ formatCurrency(row.subtotal, 2, store.get('current_event.currency_code')) }}
                    </template>
                    <template #column(total)="{ row }">
                        {{ formatCurrency(row.total, 2, store.get('current_event.currency_code')) }}
                    </template>
                </DataTable>
            </template>

            <template v-else>
                <div class="tw-flex tw-px-3 tw-pt-3 tw-pb-1">
                    <FormField class="tw-flex-1 tw-pr-1" label="Invoice Date">
                        <VueFlatPickr
                            v-model="formData.invoice_date"
                            placeholder="Required"
                            :config="flatpickrConfig"
                            class="input"
                            required
                        />
                    </FormField>

                    <FormField class="tw-flex-1 tw-pl-1" label="Due Date">
                        <VueFlatPickr
                            v-model="formData.due_date"
                            placeholder="Required"
                            :config="flatpickrConfig"
                            class="input"
                            required
                        />
                    </FormField>
                </div>

                <FormField class="tw-flex-1 tw-px-3 tw-pb-3" label="Invoice Number">
                    <Textbox v-model="formData.invoice_number" class="tw-w-full" />
                </FormField>
                <LineItemBuilder v-model="formData.items" tax-rate-type="income" />

                <FormField class="tw-mt-3 tw-p-3">
                    <Upload v-model="formData.files" :accept="['application/pdf']" multiple label="Upload" />
                    <div v-if="formData.files.length" class="tags mt-2">
                        <span v-for="(file, index) in formData.files" :key="index" class="tag is-primary">
                            {{ file.name }}
                            <button
                                class="delete is-small"
                                type="button"
                                @click.prevent="formData.files.splice(index, 1)"
                            />
                        </span>
                    </div>
                </FormField>
            </template>
        </div>

        <template #footer>
            <Button :disabled="isLoading || isUploading" @click="closeModal()">Cancel</Button>
            <FormField v-if="revenueEntry && revenueEntry.supplier" class="tw-pl-4" label="Customer">
                {{ revenueEntry.supplier.name }}
            </FormField>
            <FormField v-if="revenueEntry && step === 0" class="tw-pl-4" label="Subtotal">
                {{
                    formatCurrency(
                        selectedLineItems.reduce((total, lineItem) => total + parseFloat(lineItem.subtotal || 0), 0),
                        2,
                        store.get('current_event.currency_code')
                    )
                }}
            </FormField>
            <div class="tw-flex-1"></div>
            <FormField v-if="formData.revenue_entry_line_item_id" class="tw-text-right tw-pr-5" label="Total">
                {{ localise(totalValue, 2, true) }}
            </FormField>

            <Button v-if="step > 0" class="tw-mr-2" :disabled="isLoading || isUploading" @click="step--">Back</Button>
            <Button
                v-if="!hasSavedSupplier"
                :disabled="isLoading || isUploading || !revenueEntry?.supplier.name"
                type="is-success"
                @click="saveCustomer()"
            >
                Update Customer
            </Button>
            <Button
                v-else-if="step === 0 || step < 1"
                :disabled="isLoading || isUploading || !selectedLineItems.length"
                type="is-success"
                @click="step++"
            >
                Next
            </Button>
            <Button
                v-else
                type="is-success"
                :disabled="isLoading || isUploading || !selectedLineItems.length"
                @click="raiseInvoice()"
            >
                Raise Invoice
            </Button>
        </template>
    </Modal>
</template>
<script>
import Modal from '@/js/components/Modal.vue';
import LineItemBuilder from '@/js/components/widgets/LineItemBuilder.vue';
import Button from '@/js/components/controls/Button.vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import FormField from '@/js/components/widgets/FormField.vue';
import DataTable from '@/js/components/tables/DataTable.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import Select from '@/js/components/controls/Select.vue';
import Banner from '@/js/components/Layout/Banner.vue';
import Upload from '@/js/components/controls/Upload.vue';
import { obj2form, toast, formatCurrency } from '@/js/utils.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import ProgressBar from '@/js/components/ProgressBar.vue';
import LoadingSpinner from '@/js/components/widgets/LoadingSpinner.vue';
import VueFlatPickr from 'vue-flatpickr-component';

export default {
    components: {
        Modal,
        LineItemBuilder,
        Button,
        FormField,
        DataTable,
        Textbox,
        Select,
        Banner,
        Upload,
        ProgressBar,
        LoadingSpinner,
        VueFlatPickr,
    },
    emits: ['created', 'reload'],
    data() {
        const store = useDataStore();
        return {
            step: 0,
            isLoading: false,
            isUploading: false,
            uploadFileName: null,
            uploadProgress: 0,
            hasSavedSupplier: false,
            revenueEntry: null,
            selectedLineItems: [],
            formData: {
                invoice_date: new Date().toISOString().split('T')[0],
                due_date: new Date().toISOString().split('T')[0],
                items: [],
                files: [],
            },
            xeroTaxRates: Object.values(store.get('account.tax_rates')),
            countries: window.Locales,
            store: store,
        };
    },
    computed: {
        totalValue() {
            const subtotal = this.formData.subtotal ? parseFloat(this.formData.subtotal) : 0;
            const taxRate = this.store.get('account.tax_rates.' + this.formData.tax_rate_id);
            return subtotal + (subtotal / 100) * (taxRate ? taxRate.rate : 0);
        },
        uninvoicedLineItemCount() {
            return (
                Object.values(this.revenueEntry.line_items).filter(function (item) {
                    return item.is_invoiced === false;
                }).length ?? 0
            );
        },
        availableLineItems() {
            if (!this.revenueEntry) {
                return [];
            }
            return this.revenueEntry.line_items;
        },
        supplierIsValid() {
            if (!this.revenueEntry || !this.revenueEntry.supplier) {
                return false;
            }
            return (
                this.revenueEntry.supplier.address1 &&
                this.revenueEntry.supplier.city &&
                this.revenueEntry.supplier.postal_code &&
                this.revenueEntry.supplier.country
            );
        },
    },
    mounted() {
        Eventbus.$on('salesInvoice:create', (payload) => {
            if (payload?.entry) {
                this.loadRevenueEntry(payload.entry);
            }
            this.openModal();
        });
    },
    methods: {
        formatCurrency,
        loadRevenueEntry(revenueEntryId) {
            this.isLoading = true;
            axios
                .get(
                    route('api.account.event.revenue.get', {
                        revenueEntry: revenueEntryId,
                    })
                )
                .then((response) => {
                    this.resetModal();
                    this.revenueEntry = response.data;
                    this.isLoading = false;
                    if (this.supplierIsValid) {
                        this.hasSavedSupplier = true;
                    }
                    if (this.uninvoicedLineItemCount === 1) {
                        const item = Object.values(this.revenueEntry.line_items).filter(function (item) {
                            return item.is_invoiced === false;
                        });

                        this.selectedLineItems.push(item[0]);
                        this.loadItemsIntoForm();
                        if (this.supplierIsValid) {
                            this.step++;
                        }
                    }
                    const prefix = this.store.get('account.preferences.si_prefix');
                    this.formData.invoice_number =
                        (prefix ? prefix + '-' : '') + (this.store.get('account.last_si_number') + 1);
                    this.fetchDueDate();
                });
        },
        openModal() {
            if (this.$refs.modal) {
                this.$refs.modal.open();
            }
        },
        closeModal() {
            if (this.$refs.modal) {
                this.$refs.modal.close();
                this.resetModal();
            }
        },
        resetModal() {
            this.step = 0;
            this.isLoading = false;
            this.isUploading = false;
            this.uploadFileName = null;
            this.uploadProgress = 0;
            this.revenueEntry = null;
            this.hasSavedSupplier = false;
            this.selectedLineItems = [];
            this.formData = {
                invoice_number: '',
                invoice_date: new Date().toISOString().split('T')[0],
                due_date: new Date().toISOString().split('T')[0],
                items: [],
                files: [],
            };
        },
        loadItemsIntoForm() {
            this.formData.items = [];
            this.selectedLineItems.forEach((lineItem) => {
                this.formData.items.push({
                    revenue_entry_line_item_id: lineItem.id,
                    description: lineItem.description ?? null,
                    quantity: lineItem.quantity,
                    unit_price: parseFloat(lineItem.unit_price ?? 0).toFixed(2),
                    tax_rate_id: lineItem.tax_rate_id ?? null,
                });
            });
        },
        async raiseInvoice() {
            if (this.isLoading || this.isUploading) {
                return;
            }

            const files = [...this.formData.files];
            const revenueEntryId = this.revenueEntry.id;
            const invoiceData = { ...this.formData };
            delete invoiceData.files;

            this.isLoading = true;
            try {
                const response = await axios.post(
                    route('api.account.event.revenue.invoices.raiseInvoice', {
                        revenueEntry: revenueEntryId,
                    }),
                    obj2form(invoiceData)
                );
                capturePosthogEvent('customer_invoice_created');

                this.isLoading = false;
                this.isUploading = files.length > 0;
                const failedUploads = [];

                for (const file of files) {
                    this.uploadFileName = file.name;
                    this.uploadProgress = 0;

                    try {
                        await this.uploadAttachment(file, response.data.id, revenueEntryId);
                    } catch {
                        failedUploads.push(file.name);
                    }
                }

                data.account.last_si_number++;
                this.$emit('created', response.data.id);
                if (failedUploads.length) {
                    toast(
                        'Invoice Created',
                        `Your invoice was created, but these attachments could not be uploaded: ${failedUploads.join(', ')}`,
                        'warning'
                    );
                } else {
                    toast('Invoice Created', 'Your invoice has been created successfully', 'success');
                }
                this.closeModal();
                this.$emit('reload');
            } finally {
                this.isLoading = false;
                this.isUploading = false;
            }
        },
        async uploadAttachment(file, customerInvoiceId, revenueEntryId) {
            const routeParams = {
                event: this.revenueEntry.event_id,
                revenueEntry: revenueEntryId,
                customerInvoice: customerInvoiceId,
            };
            const { data: uploadHandshake } = await axios.post(
                route('api.account.event.revenue.invoices.files.upload', routeParams),
                {
                    name: file.name,
                    size: file.size,
                }
            );

            if (!uploadHandshake?.action) {
                throw new Error('Unable to initialise the attachment upload.');
            }

            const uploadData = new FormData();
            Object.entries(uploadHandshake.inputs).forEach(([key, value]) => uploadData.append(key, value));
            uploadData.append('key', uploadHandshake.destination);
            uploadData.append('file', file);

            await axios.post(uploadHandshake.action, uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: ({ loaded, total }) => {
                    if (total) {
                        this.uploadProgress = Math.round((loaded / total) * 100);
                    }
                },
            });
            this.uploadProgress = 100;

            await axios.post(route('api.account.event.revenue.invoices.files.completeUpload', routeParams), {
                internalSignature: uploadHandshake.internalSignature,
            });
        },
        async saveCustomer() {
            this.isLoading = true;
            if (!this.supplierIsValid) {
                const { value: confirmation } = await swal.fire({
                    title: 'Warning',
                    text: 'Are you sure you would like to continue without a full postal address?',
                    showCancelButton: true,
                    confirmButtonText: "Yes, I'm sure",
                });

                if (!confirmation) {
                    this.isLoading = false;
                    return;
                }
            }

            axios
                .patch(
                    route('api.account.contacts.update', {
                        supplier: this.revenueEntry.supplier_id,
                    }),
                    {
                        name: this.revenueEntry.supplier.name,
                        address1: this.revenueEntry.supplier.address1,
                        address2: this.revenueEntry.supplier.address2,
                        city: this.revenueEntry.supplier.city,
                        postal_code: this.revenueEntry.supplier.postal_code,
                        country: this.revenueEntry.supplier.country,
                    }
                )
                .then(() => (this.hasSavedSupplier = true))
                .finally(() => (this.isLoading = false));
        },
        fetchDueDate() {
            const defaultDueDays = this.store.get('account.preferences.days_to_sales_invoice_due');
            const invoiceDate = new Date(this.formData.invoice_date);
            invoiceDate.setDate(invoiceDate.getDate() + defaultDueDays);
            this.formData.due_date = invoiceDate.toISOString().split('T')[0];
        },
    },
};
</script>
